import {
  Loader2,
  CheckCircle2,
  Sparkles,
  AlertTriangle,
  ShieldCheck,
  Clock,
} from "lucide-react";
import { useStudyBuilderStore } from "@/stores/study-builder-store";
import { useBuildReviews } from "@/hooks/use-protocol-builder";
import { ArtifactContent } from "@/components/study-builder/ArtifactDetailViews";

export function StudyBuildDetailInspector() {
  const build = useStudyBuilderStore((s) => s.build);
  const isStreaming = useStudyBuilderStore((s) => s.isStreaming);
  const selectedArtifact = useStudyBuilderStore((s) => s.selectedArtifact);
  const buildId = useStudyBuilderStore((s) => s.buildId);
  const { data: reviewsData } = useBuildReviews(buildId ?? undefined);

  const reviewSummary = reviewsData?.reviews
    ? {
        approved: reviewsData.reviews.filter((r) => r.status === "approved").length,
        inReview: reviewsData.reviews.filter((r) => r.status === "in_review").length,
        changesRequested: reviewsData.reviews.filter((r) => r.status === "changes_requested").length,
        total: reviewsData.reviews.length,
      }
    : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      {build ? (
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground truncate">{build.studyTitle}</h2>
            <span className="shrink-0 inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
              {build.status.replace("_", " ")}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {build.protocolNumber} &middot; Phase {build.phase}
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4" />
          <span>Study Build</span>
        </div>
      )}

      {/* Streaming indicator */}
      {isStreaming && (
        <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>AI is generating...</span>
        </div>
      )}

      {/* Selected artifact detail */}
      {selectedArtifact && selectedArtifact.type !== "protocol" && (
        <div className="rounded-lg border border-border bg-card/50 overflow-y-auto max-h-[50vh]">
          <ArtifactContent
            type={selectedArtifact.type}
            artifactId={selectedArtifact.id}
          />
        </div>
      )}

      {/* Reviews summary */}
      {reviewSummary && reviewSummary.total > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          <div className="flex flex-1 items-center gap-3 text-[10px]">
            {reviewSummary.approved > 0 && (
              <span className="flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-3 w-3" />
                {reviewSummary.approved} approved
              </span>
            )}
            {reviewSummary.inReview > 0 && (
              <span className="flex items-center gap-1 text-blue-600">
                <Clock className="h-3 w-3" />
                {reviewSummary.inReview} in review
              </span>
            )}
            {reviewSummary.changesRequested > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertTriangle className="h-3 w-3" />
                {reviewSummary.changesRequested} changes
              </span>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!build && !isStreaming && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Sparkles className="h-8 w-8 text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">
            Loading study build...
          </p>
        </div>
      )}
    </div>
  );
}
